(() => {
  if (window.GeratAvailabilityAnalytics) return;

  window.GeratAvailabilityAnalytics = true;

  const trackedResultKeys = new Set();
  const trackedVisibleStores = new Set();
  let resultsTimer = null;
  let lastModalOpenAt = 0;

  const normalize = value => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const cleanText = value => String(value || '').replace(/\s+/g, ' ').trim();

  const getProductData = () => {
    const product = window.ShopifyAnalytics?.meta?.product || {};
    const selectedVariant = new URLSearchParams(window.location.search).get('variant');

    return {
      product_id: product.id,
      product_title: product.title || cleanText(document.querySelector('h1')?.textContent),
      product_handle: window.location.pathname.split('/products/')[1]?.split('/')[0],
      variant_id: selectedVariant || product.variants?.[0]?.id
    };
  };

  const cleanPayload = payload => Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

  const trackEvent = (eventName, params = {}) => {
    const payload = cleanPayload({
      event: eventName,
      event_category: 'store_availability',
      page_path: window.location.pathname,
      page_title: document.title,
      ...getProductData(),
      ...params
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    try {
      if (window.Shopify?.analytics?.publish) {
        const { event, ...shopifyPayload } = payload;
        window.Shopify.analytics.publish(event, shopifyPayload);
      }
    } catch (error) {
      // Tracking should never interrupt product-page interactions.
    }
  };

  const isLocatorElement = element => Boolean(element?.closest?.('gerat-store-locator, .gerat-store-locator'));

  const hasAvailabilitySignal = element => {
    if (!element || isLocatorElement(element)) return false;

    const attrSignal = normalize([
      element.id,
      element.className,
      element.getAttribute?.('data-block-handle'),
      element.getAttribute?.('data-section-type'),
      element.getAttribute?.('data-app-block'),
      element.getAttribute?.('aria-label')
    ].filter(Boolean).join(' '));

    if (/disponible|disponibilidad|availability|store[-_\s]?availability/.test(attrSignal)) return true;

    const text = normalize(element.textContent).slice(0, 600);
    return /disponibilidad en tienda|disponible en tienda|ver disponibilidad|piezas disponibles|selecciona.*estado/.test(text);
  };

  const isAvailabilityContext = element => {
    if (!element || isLocatorElement(element)) return false;

    let current = element;
    let depth = 0;

    while (current && current !== document.body && depth < 8) {
      if (hasAvailabilitySignal(current)) return true;
      current = current.parentElement;
      depth += 1;
    }

    return false;
  };

  const isAvailabilityButton = element => {
    const control = element?.closest?.('button, a, input[type="button"], input[type="submit"], [role="button"]');
    if (!control || isLocatorElement(control)) return null;

    const text = normalize(control.value || control.textContent || control.getAttribute('aria-label'));
    const attrSignal = normalize([control.id, control.className, control.name].filter(Boolean).join(' '));

    if (/ver disponibilidad|disponibilidad en tienda|disponible en tienda|consultar disponibilidad/.test(text)) {
      return control;
    }

    if (/disponible[-_\s]?en[-_\s]?tienda|store[-_\s]?availability|availability/.test(attrSignal) && /disponibilidad|tienda|store/.test(text || attrSignal)) {
      return control;
    }

    return null;
  };

  const getActionType = link => {
    const href = link?.getAttribute?.('href') || '';
    const text = normalize(link?.textContent || link?.getAttribute?.('aria-label'));

    if (/wa\.me|whatsapp|api\.whatsapp/.test(normalize(href)) || /whatsapp/.test(text)) return 'whatsapp';
    if (/^tel:/i.test(href) || /llamar|telefono/.test(text)) return 'call';
    if (/google\.com\/maps|maps\.app\.goo\.gl|como llegar|cómo llegar|mapa/.test(normalize(href)) || /como llegar|cómo llegar/.test(text)) return 'directions';

    return '';
  };

  const findStoreContainer = element => element?.closest?.([
    '[data-store-name]',
    '[data-location-name]',
    '[class*="store"]',
    '[class*="tienda"]',
    '[class*="sucursal"]',
    '[class*="location"]',
    'article',
    'li',
    'tr',
    '.modal',
    '[role="dialog"]'
  ].join(', '));

  const extractStoreName = element => {
    const container = findStoreContainer(element) || element;
    const dataName = container.getAttribute?.('data-store-name') || container.getAttribute?.('data-location-name');

    if (dataName) return cleanText(dataName);

    const preferred = container.querySelector?.([
      '[data-store-name]',
      '[data-location-name]',
      '[class*="store-name"]',
      '[class*="tienda-name"]',
      '[class*="sucursal-name"]',
      'h2',
      'h3',
      'h4',
      'strong'
    ].join(', '));

    const rawName = cleanText(preferred?.textContent);
    if (rawName && !/como llegar|llamar|whatsapp|disponibilidad/i.test(rawName)) return rawName.slice(0, 90);

    const fallback = cleanText(container.textContent)
      .split(/(?:Direcci[oó]n|Tel[eé]fono|Horario|Como llegar|Cómo llegar|Llamar|WhatsApp|\n)/i)[0];

    return fallback && fallback.length > 2 ? fallback.slice(0, 90) : 'No identificado';
  };

  const getAvailabilityActions = root => [...root.querySelectorAll?.('a, button, [role="button"]') || []]
    .filter(element => getActionType(element) && isAvailabilityContext(element));

  const trackResults = root => {
    const actions = getAvailabilityActions(root);
    if (!actions.length) return;

    const stores = [...new Set(actions.map(extractStoreName).filter(Boolean))];
    const resultKey = stores.join('|');

    if (!stores.length || trackedResultKeys.has(resultKey)) return;

    trackedResultKeys.add(resultKey);
    trackEvent('gerat_availability_results_view', {
      store_count: stores.length,
      store_names: stores.slice(0, 12).join(' | ')
    });

    stores.slice(0, 30).forEach(storeName => {
      const visibleKey = `${window.location.pathname}|${storeName}`;
      if (trackedVisibleStores.has(visibleKey)) return;

      trackedVisibleStores.add(visibleKey);
      trackEvent('gerat_availability_store_visible', {
        store_name: storeName
      });
    });
  };

  const queueResultsScan = root => {
    window.clearTimeout(resultsTimer);
    resultsTimer = window.setTimeout(() => trackResults(root || document.body), 500);
  };

  const trackModalOpen = () => {
    const now = Date.now();

    if (now - lastModalOpenAt < 1500) return;

    lastModalOpenAt = now;
    trackEvent('gerat_availability_modal_open');
  };

  document.addEventListener('click', event => {
    const availabilityButton = isAvailabilityButton(event.target);

    if (availabilityButton) {
      trackEvent('gerat_availability_button_click', {
        button_text: cleanText(availabilityButton.textContent || availabilityButton.value).slice(0, 80)
      });
      queueResultsScan(document.body);
      return;
    }

    const actionElement = event.target.closest?.('a, button, [role="button"]');
    const actionType = getActionType(actionElement);

    if (!actionType || !isAvailabilityContext(actionElement)) return;

    const eventNameMap = {
      directions: 'gerat_availability_get_directions',
      call: 'gerat_availability_call',
      whatsapp: 'gerat_availability_whatsapp'
    };

    trackEvent(eventNameMap[actionType], {
      store_name: extractStoreName(actionElement)
    });
  }, true);

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        if (hasAvailabilitySignal(node) || node.querySelector?.('[href^="tel:"], [href*="whatsapp"], [href*="maps"]')) {
          if (isAvailabilityContext(node) || getAvailabilityActions(node).length) {
            trackModalOpen();
            queueResultsScan(node);
            return;
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
